// The live chat API binds a thread to the visitor who started it (services-sure:
// sure_live_chat_api thread_token): the socket handshake answers with a token
// for the thread, and the thread's other requests carry it as "Bearer <token>",
// HTTP requests in the Authorization header, socket events in an `authorization` field.
export const authorization = (token) => (token ? `Bearer ${token}` : undefined);

export const authorizationHeaders = (token) =>
  token ? { Authorization: authorization(token) } : {};

// The token of the thread the widget is on. A message sent right after the socket
// connects waits for the handshake's answer, so it can carry the token, but no
// longer than the timeout given: a backend that sends no token never answers.
export const createThreadTokenStore = () => {
  let threadId;
  let token;
  let pending = null;

  const settle = () => {
    if (!pending) return;
    clearTimeout(pending.timer);
    pending.resolve();
    pending = null;
  };

  const get = (forThreadId) => (forThreadId && forThreadId === threadId ? token : undefined);

  return {
    get,

    // Switch to a thread, with the token stored for it, if any.
    use(nextThreadId, storedToken) {
      settle();
      threadId = nextThreadId;
      token = storedToken || undefined;
    },

    // A handshake for the thread is under way; sends wait for its answer from now on.
    expect(forThreadId, timeoutMs) {
      if (pending && pending.threadId !== forThreadId) settle();
      if (!forThreadId || forThreadId !== threadId) return;
      if (!pending) {
        let resolve;
        const answered = new Promise((done) => {
          resolve = done;
        });
        pending = { threadId: forThreadId, answered, resolve };
      }
      clearTimeout(pending.timer);
      pending.timer = setTimeout(settle, timeoutMs);
    },

    // The handshake's answer: the thread's token, or none when it was refused.
    answer(forThreadId, newToken) {
      if (!forThreadId || forThreadId !== threadId) return;
      if (newToken) token = newToken;
      if (pending?.threadId === forThreadId) settle();
    },

    async wait(forThreadId) {
      if (!get(forThreadId) && pending?.threadId === forThreadId) await pending.answered;
      return get(forThreadId);
    },
  };
};
